import { ApolloClient, gql as contentfulGQL } from '@apollo/client'
import { fullPublisher } from '@/constants/seo'
import { slugs } from '@/constants/slugs'
import { ContentfulClientOptions, getContentfulClient } from '@/services/ApolloClient'
import { GetProjectParams, ProjectMeta } from '@/services/ProjectsService/types'
import { GetTheatricalPreReleasePageDataQuery } from '@/types/codegen-contentful'
import { logger } from '@/utils/logging'
import { useTranslate } from '@/utils/translate/translate-client'
import { Maybe } from '@/utils/types'
import { CatalogTitle } from '../ContentCatalog/types'

export type GetTheatricalPreReleasePageDataResult = NonNullable<
  NonNullable<NonNullable<GetTheatricalPreReleasePageDataQuery['projectCollection']>['items']>[number]
> & {
  faqs: GetTheatricalPreReleasePageDataQuery['faqCollection']
}

export const getTheatricalPreReleasePageData = async (
  { slug }: GetProjectParams,
  client: ApolloClient<object>,
  opts: ContentfulClientOptions,
): Promise<GetTheatricalPreReleasePageDataResult> => {
  const contentfulClient = getContentfulClient({
    locale: opts.locale ?? 'en',
    preview: opts.preview,
  })

  let contentfulProjectData
  let contentfulFaqData

  try {
    const { data: contentfulData } = await contentfulClient.query({
      query: GET_THEATRICAL_PRE_RELEASE_PAGE_DATA_QUERY,
      variables: { projectSlug: slug, preview: opts.preview },
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    })
    contentfulProjectData = contentfulData?.projectCollection?.items?.[0]
    contentfulFaqData = contentfulData?.faqCollection?.items
  } catch (e) {
    logger().error('Failure to get theatrical pre release data from Contentful', { slug }, e)
  }

  return {
    ...contentfulProjectData,
    faqs: contentfulFaqData ?? [],
  }
}

export function isTheChosen(project: string): boolean {
  return project === slugs.theChosen
}

export function isProjectThatsBothSeriesAndMovie(projectMeta: Pick<ProjectMeta, 'slug'>): boolean {
  return (
    projectMeta.slug === slugs.homestead ||
    projectMeta.slug === slugs.truthAndConviction ||
    projectMeta.slug === slugs.truthAndTreason
  )
}

//
// NOTE: this is a temporary operation. We want to break the use of Contentful for this data. We are doing that here.
// In the near future, we change the type for catalogTitle to NonNullable, and we
// don't fallback to a generic title.
export function formatFilmName(catalogTitle: Maybe<CatalogTitle>): string {
  return catalogTitle?.title || fullPublisher.name
}

export function getCurrentYear() {
  return new Date().getUTCFullYear().toString()
}

interface useMoviesTitleProps {
  filmDetails?: NonNullable<GetTheatricalPreReleasePageDataResult>['filmDetails']
  filmName: string
  inTheaters: boolean
  isStreaming: boolean
  releaseYear?: string | null
  releaseDate?: string | null
}

export function useMoviesTitle({
  filmDetails,
  filmName,
  inTheaters,
  isStreaming,
  releaseYear,
  releaseDate,
}: useMoviesTitleProps) {
  const { t } = useTranslate('theatrical-presales')
  let title

  if (isStreaming) {
    title = t(
      'hubPageNowStreamingTitleAngel',
      '{{filmName}} ({{releaseYear}}) | Official Website | Now Streaming on Angel',
      {
        filmName,
        releaseYear,
      },
    )
  } else if (inTheaters) {
    title = t(
      'hubPageNowPlayingInTheaters',
      `{{filmName}} ({{releaseYear}}) | Official Website | Now Playing In Theaters`,
      {
        filmName,
        releaseYear,
      },
    )
  } else if (filmDetails?.theatricalReleaseDateOverrideText) {
    title = t(
      'hubPageInTheatersDateOverride',
      `{{filmName}} ({{releaseYear}}) | Official Website | In Theaters {{releaseDateOverride}}`,
      {
        filmName,
        releaseDateOverride: filmDetails?.theatricalReleaseDateOverrideText,
        releaseYear,
      },
    )
  } else if (releaseDate && releaseDate < new Date().toISOString()) {
    title = t(
      'hubPageInTheatersDate',
      `{{filmName}} ({{releaseYear}}) | Official Website | In Theaters {{releaseDate}}`,
      {
        filmName,
        releaseDate,
        releaseYear,
      },
    )
  } else if (releaseYear) {
    title = t('hubPageTitleAngel', '{{filmName}} ({{releaseYear}}) | Official Website | Angel', {
      filmName,
      releaseYear,
    })
  } else {
    title = t('hubPageTitleBaseAngel', '{{filmName}} | Official Website | Angel', {
      filmName,
    })
  }

  return title
}

interface useSeriesTitleProps {
  filmName: string
  isStreaming: boolean
  releaseYear?: string | null
}

export function useSeriesTitle({ filmName, isStreaming, releaseYear }: useSeriesTitleProps) {
  const { t } = useTranslate('theatrical-presales')
  let title

  if (isStreaming && releaseYear) {
    title = t(
      'hubPageNowStreamingTitleAngel',
      '{{filmName}} ({{releaseYear}}) | Official Website | Now Streaming on Angel',
      {
        filmName,
        releaseYear,
      },
    )
  } else if (releaseYear) {
    title = t('hubPageTitleAngel', '{{filmName}} ({{releaseYear}}) | Official Website | Angel', {
      filmName,
      releaseYear,
    })
  } else {
    title = t('hubPageTitleBaseAngel', '{{filmName}} | Official Website | Angel', {
      filmName,
      releaseYear,
    })
  }

  return title
}

interface useDigitalTitleProps {
  filmName: string
  releaseYear?: string | null
}

export function useDigitalTitle({ filmName, releaseYear }: useDigitalTitleProps) {
  const { t } = useTranslate('theatrical-presales')

  return t(
    'digitalPageNowStreamingTitle',
    '{{filmName}} ({{releaseYear}}) | Official Website | Where To Stream {{filmName}}',
    {
      filmName,
      releaseYear,
    },
  )
}

export const GET_THEATRICAL_PRE_RELEASE_PAGE_DATA_QUERY = contentfulGQL/* GraphQL */ `
  fragment InvestmentBannerItems on InvestmentBanner {
    sys {
      id
      publishedVersion
    }
    mobileBackgroundImageCloudinaryPath
    tabletBackgroundImageCloudinaryPath
    desktopBackgroundImageCloudinaryPath
    gravity
    title
    description
    ctaText
    offeringUrl
    interstitialText
    interstitialButtonText
    trailerYouTubeId
    subsidiaryName
    logoCloudinaryPath
  }

  query getTheatricalPreReleasePageData($projectSlug: String!, $locale: String, $preview: Boolean) {
    projectCollection(limit: 1, where: { slug: $projectSlug }, locale: $locale, preview: $preview) {
      items {
        slug
        contentCatalogId
        crowdfundingSlug
        showOverview
        showMetaDescription
        distributionStage
        logo
        name
        thumbnail
        hasPressPage
        hasAmbassadorPage
        heroCta {
          sys {
            id
          }
          type
          emailListId
          buttonText
          guildButtonText
          buttonHref
          guildButtonHref
          description
        }
        investmentBanner {
          ...InvestmentBannerItems
        }
        ctaSquaresCollection(limit: 9) {
          items {
            sys {
              id
            }
            title
            description
            icon
            backgroundImage
            type
            emailListId
            buttonText
            guildButtonText
            buttonHref
            guildButtonHref
            marketingFocus
          }
        }
        filmDetails {
          additionalVideosCollection {
            items {
              link
              title
              thumbnail
            }
          }
          angelInvestors
          brandColor
          filmAssociationLogos
          heroBackgroundVideo
          heroBackgroundImage
          imagesCollection {
            items {
              altText
              item
            }
          }
          livestreamImageUrl
          livestreamDescription
          livestreamButtonText
          livestreamButtonHref
          moviePoster
          releaseYear
          sys {
            id
          }
          theatricalReleaseDate
          theatricalReleaseDateOverrideText
          trailerSourceUrl
          shareOverrideUrl
          willHaveTheatricalRelease
          statsBox {
            sys {
              id
            }
            logoImageUrl
            statsDescription
            guildStatsDescription
          }
        }
        shopifyStore {
          storeUrl
          accessToken
          handle
          storeHref
        }
        rottenTomatoes: criticsCollection(limit: 1, where: { sort: "Rotten Tomatoes" }) {
          items {
            rating
            author
            icon
          }
        }
        audience: criticsCollection(limit: 1, where: { sort: "Audience Scores" }) {
          items {
            rating
            author
            icon
          }
        }
        cinema: criticsCollection(limit: 1, where: { sort: "CinemaScore" }) {
          items {
            rating
            author
            icon
          }
        }
        IMDb: criticsCollection(limit: 1, where: { sort: "IMDb" }) {
          items {
            rating
            author
            icon
          }
        }
        critics: criticsCollection(where: { sort_exists: false }) {
          items {
            author
            company
            rating
            quote
          }
        }
      }
    }
    faqCollection(
      where: { location: "watch", project: { slug: $projectSlug } }
      locale: $locale
      preview: $preview
      order: ordinal_ASC
    ) {
      items {
        sys {
          id
        }
        answer {
          json
        }
        ordinal
        question
      }
    }
  }
`
